<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { previewIvrMenu, saveIvrMenu } from 'dashboard/api/relayVoiceManager';
import {
  emptyMenu,
  EXIT_TYPES,
  menuFromSpec,
  menuPayload,
  menuProblems,
  OPTION_TYPES,
} from './helpers';

const props = defineProps({
  menuUuid: { type: String, default: null },
  spec: { type: Object, default: null },
  targets: { type: Object, default: () => ({}) },
  prompts: { type: Array, default: () => [] },
});
const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();
const { accountId } = useAccount();
const menu = ref(props.spec ? menuFromSpec(props.spec) : emptyMenu());
const serverErrors = ref([]);
const preview = ref([]);
const saving = ref(false);
const previewing = ref(false);
const touched = ref(false);

const problems = computed(() => menuProblems(menu.value));
const problemText = problem =>
  t(`TEAM_LEAD.IVR.PROBLEMS.${problem.key}`, problem);
const targetOptions = type =>
  (props.targets[type] || []).map(row => ({
    value: row.uuid,
    label: `${row.name} (${row.number})`,
  }));
const typeLabel = type => t(`TEAM_LEAD.IVR.TYPES.${type.toUpperCase()}`);
const fieldId = name => `ivr-${props.menuUuid || 'new'}-${name}`;

const addOption = () => {
  const used = new Set(menu.value.options.map(o => o.digits));
  const free = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].find(
    d => !used.has(d)
  );
  menu.value.options.push({
    digits: free || '',
    type: 'extension',
    target: '',
    description: '',
  });
};
const removeOption = index => menu.value.options.splice(index, 1);

const request = async (kind, action) => {
  touched.value = true;
  serverErrors.value = [];
  if (problems.value.length) return null;
  try {
    return await action(menuPayload(menu.value));
  } catch (error) {
    const body = error.response?.data || {};
    serverErrors.value = body.errors || [
      body.error || t('TEAM_LEAD.GENERIC_ERROR'),
    ];
    if (kind === 'preview') preview.value = [];
    return null;
  }
};

const runPreview = async () => {
  previewing.value = true;
  const result = await request('preview', payload =>
    previewIvrMenu(accountId.value, {
      ...payload,
      ...(props.menuUuid ? { ivr_menu_uuid: props.menuUuid } : {}),
    })
  );
  if (result) preview.value = result.preview;
  previewing.value = false;
};

const save = async () => {
  saving.value = true;
  const result = await request('save', payload =>
    saveIvrMenu(accountId.value, payload, props.menuUuid)
  );
  saving.value = false;
  if (result) {
    preview.value = result.preview || [];
    useAlert(t('TEAM_LEAD.IVR.SAVED'));
    emit('saved', result.ivr_menu_uuid);
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-5"
    data-test-id="ivr-editor"
    novalidate
    @submit.prevent="save"
  >
    <div class="grid gap-4 sm:grid-cols-2">
      <Input
        v-model="menu.name"
        :label="t('TEAM_LEAD.IVR.NAME')"
        :placeholder="t('TEAM_LEAD.IVR.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="menu.extension"
        :label="t('TEAM_LEAD.IVR.NUMBER')"
        :placeholder="t('TEAM_LEAD.IVR.NUMBER_PLACEHOLDER')"
        :message="t('TEAM_LEAD.IVR.NUMBER_HINT')"
      />
    </div>

    <fieldset class="flex flex-col gap-3" data-tour="ivr-greeting">
      <legend class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ t('TEAM_LEAD.IVR.PROMPTS_TITLE') }}
      </legend>
      <p v-if="!prompts.length" class="text-sm text-n-amber-11">
        {{ t('TEAM_LEAD.IVR.NO_PROMPTS') }}
      </p>
      <div class="grid gap-4 sm:grid-cols-2">
        <label
          v-for="field in [
            'greeting',
            'short_greeting',
            'invalid_sound',
            'exit_sound',
          ]"
          :key="field"
          class="flex flex-col gap-1 text-sm text-n-slate-12"
          :for="fieldId(field)"
        >
          {{ t(`TEAM_LEAD.IVR.FIELDS.${field.toUpperCase()}`) }}
          <select
            :id="fieldId(field)"
            v-model="menu[field]"
            class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
          >
            <option value="">
              {{
                field === 'greeting'
                  ? t('TEAM_LEAD.PROMPTS.CHOOSE')
                  : t('TEAM_LEAD.IVR.NONE')
              }}
            </option>
            <option v-for="name in prompts" :key="name" :value="name">
              {{ name }}
            </option>
          </select>
        </label>
      </div>
    </fieldset>

    <fieldset class="flex flex-col gap-3" data-tour="ivr-options">
      <legend class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ t('TEAM_LEAD.IVR.OPTIONS_TITLE') }}
      </legend>
      <div
        v-for="(option, index) in menu.options"
        :key="index"
        class="grid gap-2 sm:grid-cols-[5rem_10rem_1fr_auto] items-end rounded-lg bg-n-alpha-1 p-3"
      >
        <Input
          v-model="option.digits"
          :label="t('TEAM_LEAD.IVR.KEY')"
          :aria-label="t('TEAM_LEAD.IVR.KEY_FOR', { n: index + 1 })"
        />
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TEAM_LEAD.IVR.DESTINATION') }}
          <select
            v-model="option.type"
            class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
            @change="option.target = ''"
          >
            <option v-for="type in OPTION_TYPES" :key="type" :value="type">
              {{ typeLabel(type) }}
            </option>
          </select>
        </label>
        <label
          v-if="option.type !== 'repeat'"
          class="flex flex-col gap-1 text-sm text-n-slate-12 min-w-0"
        >
          {{ t('TEAM_LEAD.IVR.TARGET') }}
          <select
            v-model="option.target"
            class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
          >
            <option value="" disabled>
              {{
                targetOptions(option.type).length
                  ? t('TEAM_LEAD.PROMPTS.CHOOSE')
                  : t('TEAM_LEAD.IVR.NO_TARGETS', {
                      type: typeLabel(option.type),
                    })
              }}
            </option>
            <option
              v-for="target in targetOptions(option.type)"
              :key="target.value"
              :value="target.value"
            >
              {{ target.label }}
            </option>
          </select>
        </label>
        <p v-else class="text-sm text-n-slate-11 pb-2">
          {{ t('TEAM_LEAD.IVR.REPEAT_HINT') }}
        </p>
        <Button
          type="button"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="ruby"
          size="sm"
          :disabled="menu.options.length === 1"
          :aria-label="t('TEAM_LEAD.IVR.REMOVE_OPTION', { n: index + 1 })"
          @click="removeOption(index)"
        />
      </div>
      <Button
        type="button"
        :label="t('TEAM_LEAD.IVR.ADD_OPTION')"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        class="w-fit"
        :disabled="menu.options.length >= 12"
        @click="addOption"
      />
    </fieldset>

    <fieldset class="flex flex-col gap-3" data-tour="ivr-timing">
      <legend class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ t('TEAM_LEAD.IVR.WHEN_STUCK') }}
      </legend>
      <div class="grid gap-4 sm:grid-cols-3">
        <Input
          v-model="menu.timeout_seconds"
          type="number"
          min="1"
          max="30"
          :label="t('TEAM_LEAD.IVR.TIMEOUT')"
        />
        <Input
          v-model="menu.max_timeouts"
          type="number"
          min="1"
          max="5"
          :label="t('TEAM_LEAD.IVR.MAX_TIMEOUTS')"
        />
        <Input
          v-model="menu.max_failures"
          type="number"
          min="1"
          max="5"
          :label="t('TEAM_LEAD.IVR.MAX_FAILURES')"
        />
      </div>
      <div class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TEAM_LEAD.IVR.FINALLY') }}
          <select
            v-model="menu.exit.type"
            class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
            @change="menu.exit.target = ''"
          >
            <option v-for="type in EXIT_TYPES" :key="type" :value="type">
              {{ typeLabel(type) }}
            </option>
          </select>
        </label>
        <label
          v-if="menu.exit.type !== 'hangup'"
          class="flex flex-col gap-1 text-sm text-n-slate-12"
        >
          {{ t('TEAM_LEAD.IVR.TARGET') }}
          <select
            v-model="menu.exit.target"
            class="!mb-0 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak px-3 py-2 text-sm"
          >
            <option value="" disabled>
              {{ t('TEAM_LEAD.PROMPTS.CHOOSE') }}
            </option>
            <option
              v-for="target in targetOptions(menu.exit.type)"
              :key="target.value"
              :value="target.value"
            >
              {{ target.label }}
            </option>
          </select>
        </label>
      </div>
      <div class="flex items-center gap-3 text-sm text-n-slate-12">
        <Switch v-model="menu.enabled" />
        {{ t('TEAM_LEAD.IVR.ENABLED') }}
      </div>
    </fieldset>

    <div
      v-if="touched && (problems.length || serverErrors.length)"
      role="alert"
      class="rounded-lg bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
      data-test-id="ivr-problems"
    >
      <ul class="list-disc ms-4 flex flex-col gap-1">
        <li v-for="problem in problems" :key="problemText(problem)">
          {{ problemText(problem) }}
        </li>
        <li v-for="error in serverErrors" :key="error">{{ error }}</li>
      </ul>
    </div>

    <div
      v-if="preview.length"
      class="rounded-lg bg-n-alpha-1 px-4 py-3"
      data-test-id="ivr-preview"
    >
      <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
        {{ t('TEAM_LEAD.IVR.PREVIEW_TITLE') }}
      </h4>
      <ol class="list-decimal ms-4 flex flex-col gap-1 text-sm text-n-slate-11">
        <li v-for="line in preview" :key="line">{{ line }}</li>
      </ol>
      <p class="mt-2 text-xs text-n-slate-10">
        {{ t('TEAM_LEAD.IVR.PREVIEW_DIAL', { number: menu.extension }) }}
      </p>
    </div>

    <div
      class="flex items-center justify-end gap-2 border-t border-n-weak pt-4"
    >
      <Button
        type="button"
        :label="t('TEAM_LEAD.CANCEL')"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('cancel')"
      />
      <Button
        type="button"
        :label="t('TEAM_LEAD.IVR.PREVIEW')"
        variant="faded"
        size="sm"
        :is-loading="previewing"
        data-tour="ivr-preview"
        @click="runPreview"
      />
      <Button
        type="submit"
        :label="t('TEAM_LEAD.IVR.SAVE')"
        size="sm"
        :is-loading="saving"
      />
    </div>
  </form>
</template>
